import { ref, onMounted, onUnmounted, watch, nextTick, computed } from 'vue'
import { usePermissionsView } from './usePermissionsView'
import type { AccessRolePermissionGroupProps } from '../components/lists/AccessRolePermissionGroup.vue'
import { permissionGroups as typedPermissionGroups } from '../access-control-text'
import { filterVisibleGroups } from '../model/permission-view'

type ClosestGroup = { name: string; distance: number }

/**
 * Scroll-spy for the access-role permission groups: tracks which group(s) are
 * in the scrollable container's viewport (`activeGroupNames`) and scrolls a
 * group into view on demand (`handleClick`). Shared by the routed
 * `AccessRoleSidebarScrollToMenu` (rendered as a `SelectMenu`) and the
 * AdministrationModal `AccessRolePermissionsSectionMenu` (rendered with shadcn
 * `SidebarMenu` primitives). Must be called from a component under a
 * `providePermissionsView` ancestor (reads the search text to hide filtered
 * groups).
 */
export function useScrollToPermissionGroup() {
  const permissionGroups: AccessRolePermissionGroupProps[] = Object.entries(
    typedPermissionGroups
  ).map(([groupName, groupMetadata]) => ({
    groupName,
    icon: groupMetadata.icon,
    description: groupMetadata.description ?? '',
    permissionItems: groupMetadata.permissionItems,
    searchWords: [groupName, ...(groupMetadata.description?.split(' ') ?? [])]
  }))

  const scrollOffset = 20 // Adjust this value to change the offset
  // Highlight mode: 'closest' highlights the single closest domain to scroll top,
  // 'viewport' highlights all domains in viewport.
  const highlightMode = ref<'closest' | 'viewport'>('viewport')
  const activeGroupNames = ref<Set<string>>(new Set())
  let scrollContainer: HTMLElement | Window | null = null
  let scrollHandler: (() => void) | null = null

  function findScrollableContainer(element: HTMLElement): HTMLElement | Window {
    let parent = element.parentElement
    while (parent) {
      const style = window.getComputedStyle(parent)
      const overflow = style.overflowY || style.overflow
      if (
        overflow === 'auto' ||
        overflow === 'scroll' ||
        overflow === 'overlay'
      ) {
        return parent
      }
      parent = parent.parentElement
    }
    return window
  }

  // Highlight all domains that are within the viewport
  function updateActiveGroupViewport() {
    if (permissionGroups.length === 0) return

    const activeGroups = new Set<string>()

    // Get viewport bounds
    let viewportTop: number
    let viewportBottom: number

    if (scrollContainer === window) {
      viewportTop = 0
      viewportBottom = window.innerHeight
    } else {
      const container = scrollContainer as HTMLElement
      viewportTop = 0
      viewportBottom = container.clientHeight
    }

    // Find all groups that are within the viewport
    for (const group of permissionGroups) {
      const element = document.getElementById(group.groupName)
      if (!element) continue

      const rect = element.getBoundingClientRect()
      const containerRect =
        scrollContainer === window
          ? { top: 0, bottom: window.innerHeight }
          : (scrollContainer as HTMLElement).getBoundingClientRect()

      // Calculate element position relative to container
      const elementTop = rect.top - containerRect.top
      const elementBottom = rect.bottom - containerRect.top

      // Check if element is within viewport (at least partially visible)
      if (elementBottom >= viewportTop && elementTop <= viewportBottom) {
        activeGroups.add(group.groupName)
      }
    }

    activeGroupNames.value = activeGroups
  }

  // Check if we're at the bottom of the scrollable container
  function isScrolledToBottom(): boolean {
    if (!scrollContainer) return false

    if (scrollContainer === window) {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const scrollHeight = document.documentElement.scrollHeight
      const clientHeight = window.innerHeight
      return scrollTop + clientHeight >= scrollHeight - 10 // 10px threshold
    }

    const container = scrollContainer as HTMLElement
    return (
      container.scrollTop + container.clientHeight >=
      container.scrollHeight - 10 // 10px threshold
    )
  }

  // Find the group nearest the scroll top, preferring elements at or above the
  // "at the top" threshold
  function findClosestGroup(): ClosestGroup | null {
    const threshold = scrollOffset + 50
    let closestGroup: ClosestGroup | null = null

    for (const group of permissionGroups) {
      const element = document.getElementById(group.groupName)
      if (!element) continue

      const rect = element.getBoundingClientRect()
      const containerRect =
        scrollContainer === window
          ? { top: 0 }
          : (scrollContainer as HTMLElement).getBoundingClientRect()

      const elementTop = rect.top - containerRect.top
      const distance = Math.abs(elementTop - scrollOffset)

      if (elementTop <= threshold) {
        if (!closestGroup || distance < closestGroup.distance) {
          closestGroup = { name: group.groupName, distance }
        }
      } else if (!closestGroup && elementTop > 0) {
        // If no element is above threshold, use the first visible one
        closestGroup = { name: group.groupName, distance }
      }
    }

    return closestGroup
  }

  // Highlight the single domain that is closest to the scroll top
  function updateActiveGroupClosest() {
    if (permissionGroups.length === 0) return

    // If at bottom, highlight the last element
    if (isScrolledToBottom()) {
      const lastGroup = permissionGroups[permissionGroups.length - 1]
      if (lastGroup) {
        activeGroupNames.value = new Set([lastGroup.groupName])
      }
      return
    }

    const closestGroup = findClosestGroup()
    activeGroupNames.value = closestGroup
      ? new Set([closestGroup.name])
      : new Set()
  }

  // Main function that calls the appropriate update function based on highlight mode
  function updateActiveGroup() {
    if (highlightMode.value === 'viewport') {
      updateActiveGroupViewport()
    } else {
      updateActiveGroupClosest()
    }
  }

  function handleClick(groupName: string) {
    const element = document.getElementById(groupName)
    if (!element) return

    const container = findScrollableContainer(element)
    const elementRect = element.getBoundingClientRect()
    const containerRect =
      container === window
        ? { top: 0, left: 0 }
        : (container as HTMLElement).getBoundingClientRect()

    const scrollTop =
      container === window
        ? window.pageYOffset || document.documentElement.scrollTop
        : (container as HTMLElement).scrollTop

    const targetScrollTop =
      scrollTop + elementRect.top - containerRect.top - scrollOffset

    if (container === window) {
      window.scrollTo({
        top: targetScrollTop,
        behavior: 'smooth'
      })
    } else {
      ;(container as HTMLElement).scrollTo({
        top: targetScrollTop,
        behavior: 'smooth'
      })
    }
  }

  function setupScrollListener() {
    // Find the scrollable container by checking the first permission group element
    const firstGroup = permissionGroups[0]
    if (firstGroup) {
      const element = document.getElementById(firstGroup.groupName)
      if (element) {
        scrollContainer = findScrollableContainer(element)
      }
    }

    if (!scrollContainer) {
      scrollContainer = window
    }

    // Create throttled scroll handler
    let ticking = false
    scrollHandler = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateActiveGroup()
          ticking = false
        })
        ticking = true
      }
    }

    // Initial update
    updateActiveGroup()

    // Add scroll listener
    if (scrollContainer === window) {
      window.addEventListener('scroll', scrollHandler, { passive: true })
    } else {
      ;(scrollContainer as HTMLElement).addEventListener(
        'scroll',
        scrollHandler,
        { passive: true }
      )
    }
  }

  onMounted(() => {
    // Wait for next tick to ensure DOM is ready
    setTimeout(() => {
      setupScrollListener()
    }, 100)
  })

  onUnmounted(() => {
    if (scrollHandler && scrollContainer) {
      if (scrollContainer === window) {
        window.removeEventListener('scroll', scrollHandler)
      } else {
        ;(scrollContainer as HTMLElement).removeEventListener(
          'scroll',
          scrollHandler
        )
      }
    }
  })

  const { search } = usePermissionsView()

  const _hiddenGroupNames = ref<string[]>([])

  watch(search, (newValue) => {
    _hiddenGroupNames.value = []
    if (newValue) {
      nextTick(() => {
        const hiddenGroupNames: string[] = []
        permissionGroups.forEach((group) => {
          const element = document.getElementById(group.groupName)
          const isHidden = element?.classList.contains('hidden')
          if (isHidden) {
            hiddenGroupNames.push(group.groupName)
          }
        })
        _hiddenGroupNames.value = hiddenGroupNames
      })
    }
  })

  const visiblePermissionGroups = computed(() => {
    return filterVisibleGroups(permissionGroups, _hiddenGroupNames.value)
  })

  return {
    visiblePermissionGroups,
    activeGroupNames,
    handleClick
  }
}
